/* Phase SC-2 · Auto-fund rules section
 *
 * Mounted inside SellerCreditPanel. Renders the list of buyers the seller has
 * opted-in to auto-factor, plus the "Add buyer" picker. Each row shows the
 * per-buyer caps, today/30d usage, dispute-pause state, and inline controls.
 *
 * No client-side defaults — every cap field is required from the seller (per
 * approved design).
 */
import { useEffect, useMemo, useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Zap, Plus, Trash2, AlertTriangle, Pause, Play, PencilLine, Users,
} from "lucide-react";
import { toast } from "sonner";

export default function AutoFundRulesSection() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/auto-fund/rules");
      setRules(data.rows || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-border p-6 text-sm text-foreground/55" data-testid="auto-fund-loading">
        Loading auto-fund rules…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="auto-fund-rules-section">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-violet-700 inline-flex items-center gap-1.5">
            <Zap className="w-3 h-3"/> Phase SC-2 · scaled origination
          </div>
          <h3 className="display text-lg mt-1">Per-buyer auto-fund</h3>
          <p className="text-[12px] text-foreground/55 mt-0.5">
            Pick the buyers you want auto-factored. The moment they accept Net-X terms on an invoice, we get you paid — no manual click.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}
          data-testid="auto-fund-add-btn"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90">
          <Plus className="w-4 h-4 mr-1"/>Add buyer
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="p-10 text-center" data-testid="auto-fund-empty">
          <Users className="w-10 h-10 mx-auto text-foreground/30 mb-3"/>
          <h3 className="display text-lg">No buyers on auto-fund yet</h3>
          <p className="text-sm text-foreground/55 mt-1.5 max-w-md mx-auto leading-relaxed">
            Pick a buyer you trust (e.g. your biggest customer on Net-60) and we'll auto-purchase every new invoice from them within your caps.
          </p>
          <Button onClick={() => setAddOpen(true)}
            data-testid="auto-fund-empty-cta"
            className="mt-4 rounded-full bg-violet-600 text-white hover:bg-violet-700">
            <Zap className="w-4 h-4 mr-1.5"/>Set up your first rule
          </Button>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-5 py-2.5 font-medium">Buyer</th>
              <th className="px-5 py-2.5 font-medium">Min invoice</th>
              <th className="px-5 py-2.5 font-medium">Today</th>
              <th className="px-5 py-2.5 font-medium">30d</th>
              <th className="px-5 py-2.5 font-medium">Status</th>
              <th className="px-5 py-2.5"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rules.map(r => (
              <RuleRow
                key={r.id}
                rule={r}
                onChanged={load}
                onEdit={() => setEditTarget(r)}
              />
            ))}
          </tbody>
        </table>
      )}

      <AddRuleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => { setAddOpen(false); load(); }}
        existingBuyerIds={rules.map(r => r.buyer_id)}
      />

      <EditRuleDialog
        rule={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); }}
      />
    </div>
  );
}

/* ─── Row ────────────────────────────────────────────────────────────── */
function RuleRow({ rule, onChanged, onEdit }) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/seller/auto-fund/rules/${rule.id}`, { enabled: !rule.enabled });
      toast.success(rule.enabled ? "Auto-fund paused" : "Auto-fund enabled");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/auto-fund/rules/${rule.id}/resume`);
      toast.success("Auto-fund resumed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Resume failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove auto-fund rule for ${rule.buyer_company}? You can re-add it later.`)) return;
    setBusy(true);
    try {
      await api.delete(`/seller/auto-fund/rules/${rule.id}`);
      toast.success("Rule removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const dailyPct = rule.daily_cap_usd > 0
    ? Math.min(100, Math.round((rule.used_today_usd / rule.daily_cap_usd) * 100))
    : 0;
  const monthlyPct = rule.monthly_cap_usd > 0
    ? Math.min(100, Math.round((rule.used_30d_usd / rule.monthly_cap_usd) * 100))
    : 0;

  return (
    <tr data-testid={`auto-fund-row-${rule.buyer_id}`}>
      <td className="px-5 py-3.5">
        <div className="font-medium">{rule.buyer_company}</div>
        <div className="text-[11px] text-foreground/45 mono mt-0.5">id: {rule.buyer_id.slice(0,8)}</div>
      </td>
      <td className="px-5 py-3.5 tabular-nums text-foreground/75">{money(rule.min_invoice_usd)}</td>
      <td className="px-5 py-3.5">
        <div className="tabular-nums text-[12.5px]">{money(rule.used_today_usd)} <span className="text-foreground/40">/ {money(rule.daily_cap_usd)}</span></div>
        <div className="mt-1 h-1 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-violet-500" style={{width: `${dailyPct}%`}}/>
        </div>
      </td>
      <td className="px-5 py-3.5">
        <div className="tabular-nums text-[12.5px]">{money(rule.used_30d_usd)} <span className="text-foreground/40">/ {money(rule.monthly_cap_usd)}</span></div>
        <div className="mt-1 h-1 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-violet-500" style={{width: `${monthlyPct}%`}}/>
        </div>
      </td>
      <td className="px-5 py-3.5">
        {rule.paused_due_to_dispute ? (
          <div data-testid={`rule-paused-${rule.buyer_id}`}>
            <span className="chip bg-amber-50 text-amber-800 border border-amber-200 text-[10.5px] inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3"/>Paused · dispute
            </span>
            <div className="text-[10.5px] text-foreground/55 mt-1 max-w-[180px] truncate">{rule.paused_reason}</div>
          </div>
        ) : rule.enabled ? (
          <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10.5px] inline-flex items-center gap-1" data-testid={`rule-on-${rule.buyer_id}`}>
            <Zap className="w-3 h-3"/>Active
          </span>
        ) : (
          <span className="chip bg-secondary text-foreground/65 text-[10.5px]" data-testid={`rule-off-${rule.buyer_id}`}>Paused</span>
        )}
      </td>
      <td className="px-5 py-3.5 text-right">
        <div className="inline-flex gap-1.5">
          {rule.paused_due_to_dispute ? (
            <button onClick={resume} disabled={busy}
              data-testid={`rule-resume-${rule.buyer_id}`}
              className="w-7 h-7 grid place-items-center rounded-full hover:bg-emerald-50 text-emerald-700">
              <Play className="w-3.5 h-3.5"/>
            </button>
          ) : (
            <button onClick={toggle} disabled={busy}
              data-testid={`rule-toggle-${rule.buyer_id}`}
              className="w-7 h-7 grid place-items-center rounded-full hover:bg-secondary text-foreground/60">
              {rule.enabled ? <Pause className="w-3.5 h-3.5"/> : <Play className="w-3.5 h-3.5"/>}
            </button>
          )}
          <button onClick={onEdit} disabled={busy}
            data-testid={`rule-edit-${rule.buyer_id}`}
            className="w-7 h-7 grid place-items-center rounded-full hover:bg-secondary text-foreground/60">
            <PencilLine className="w-3.5 h-3.5"/>
          </button>
          <button onClick={remove} disabled={busy}
            data-testid={`rule-delete-${rule.buyer_id}`}
            className="w-7 h-7 grid place-items-center rounded-full hover:bg-rose-50 text-rose-600">
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ─── Add dialog ────────────────────────────────────────────────────── */
function AddRuleDialog({ open, onClose, onCreated, existingBuyerIds }) {
  const [buyers, setBuyers] = useState([]);
  const [buyerId, setBuyerId] = useState("");
  const [minInvoice, setMinInvoice] = useState("");
  const [dailyCap, setDailyCap] = useState("");
  const [monthlyCap, setMonthlyCap] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/tenant/connections")
      .then(r => {
        // Map connections → buyer-shaped objects for the <select>
        const conns = (r.data || []).filter(c => c.status === "active" && c.buyer_id);
        setBuyers(conns.map(c => ({
          id:      c.buyer_id,
          name:    c.buyer_name,
          email:   c.buyer_email,
          company: c.buyer_company,
        })));
      })
      .catch(() => setBuyers([]));
    setBuyerId("");
    setMinInvoice("");
    setDailyCap("");
    setMonthlyCap("");
  }, [open]);

  const eligibleBuyers = useMemo(
    () => buyers.filter(b => !existingBuyerIds.includes(b.id)),
    [buyers, existingBuyerIds],
  );

  const valid = (
    buyerId &&
    minInvoice !== "" && Number(minInvoice) >= 0 &&
    dailyCap !== "" && Number(dailyCap) > 0 &&
    monthlyCap !== "" && Number(monthlyCap) > 0 &&
    Number(dailyCap) <= Number(monthlyCap)
  );

  const submit = async () => {
    if (!valid) return toast.error("Fill every field. Daily cap can't exceed monthly cap.");
    setSubmitting(true);
    try {
      await api.post("/seller/auto-fund/rules", {
        buyer_id:        buyerId,
        enabled:         true,
        min_invoice_usd: Number(minInvoice),
        daily_cap_usd:   Number(dailyCap),
        monthly_cap_usd: Number(monthlyCap),
      });
      toast.success("Auto-fund rule created");
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.reason || "Could not create rule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="auto-fund-add-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl">Add buyer to auto-fund</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Buyer</Label>
            <select
              value={buyerId}
              onChange={e => setBuyerId(e.target.value)}
              data-testid="auto-fund-buyer-select"
              className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Pick a buyer…</option>
              {eligibleBuyers.map(b => (
                <option key={b.id} value={b.id}>{b.company || b.name || b.email}</option>
              ))}
            </select>
            {eligibleBuyers.length === 0 && (
              <p className="text-[11px] text-foreground/50 mt-1.5">
                No connected buyers available {buyers.length === 0 ? "yet — invite a buyer from the Buyers page" : "— every connected buyer already has a rule"}.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Min invoice (USD)</Label>
              <Input type="number" min={0} step={100} value={minInvoice}
                onChange={e => setMinInvoice(e.target.value)}
                data-testid="auto-fund-min"
                className="mt-1.5 h-11"/>
              <p className="text-[10.5px] text-foreground/45 mt-1">Skip invoices below this</p>
            </div>
            <div>
              <Label>Daily cap (USD)</Label>
              <Input type="number" min={1} step={1000} value={dailyCap}
                onChange={e => setDailyCap(e.target.value)}
                data-testid="auto-fund-daily-cap"
                className="mt-1.5 h-11"/>
              <p className="text-[10.5px] text-foreground/45 mt-1">Max funded per day</p>
            </div>
            <div>
              <Label>Monthly cap (USD)</Label>
              <Input type="number" min={1} step={5000} value={monthlyCap}
                onChange={e => setMonthlyCap(e.target.value)}
                data-testid="auto-fund-monthly-cap"
                className="mt-1.5 h-11"/>
              <p className="text-[10.5px] text-foreground/45 mt-1">Max in 30 days</p>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 text-[12px] text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
            <div>
              If any invoice from this buyer enters dispute, this rule auto-pauses
              and you'll need to manually resume after the dispute closes.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={!valid || submitting}
            data-testid="auto-fund-submit"
            className="rounded-full bg-violet-600 text-white hover:bg-violet-700">
            {submitting ? "Creating…" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Edit dialog ───────────────────────────────────────────────────── */
function EditRuleDialog({ rule, onClose, onSaved }) {
  const [minInvoice, setMinInvoice] = useState("");
  const [dailyCap, setDailyCap] = useState("");
  const [monthlyCap, setMonthlyCap] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!rule) return;
    setMinInvoice(String(rule.min_invoice_usd ?? ""));
    setDailyCap(String(rule.daily_cap_usd ?? ""));
    setMonthlyCap(String(rule.monthly_cap_usd ?? ""));
    setEnabled(!!rule.enabled);
  }, [rule]);

  if (!rule) return null;

  const valid = (
    minInvoice !== "" && Number(minInvoice) >= 0 &&
    dailyCap !== "" && Number(dailyCap) > 0 &&
    monthlyCap !== "" && Number(monthlyCap) > 0 &&
    Number(dailyCap) <= Number(monthlyCap)
  );

  const submit = async () => {
    if (!valid) return toast.error("Fill every field. Daily cap can't exceed monthly cap.");
    setSubmitting(true);
    try {
      await api.patch(`/seller/auto-fund/rules/${rule.id}`, {
        enabled:         enabled,
        min_invoice_usd: Number(minInvoice),
        daily_cap_usd:   Number(dailyCap),
        monthly_cap_usd: Number(monthlyCap),
      });
      toast.success("Rule updated");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.reason || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!rule} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="auto-fund-edit-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl">Edit auto-fund · {rule.buyer_company}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 px-4 py-3">
            <div>
              <Label className="text-foreground">Rule enabled</Label>
              <p className="text-[11px] text-foreground/55 mt-0.5">Toggle off to pause without losing the caps.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="auto-fund-edit-enabled"/>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Min invoice (USD)</Label>
              <Input type="number" min={0} step={100} value={minInvoice}
                onChange={e => setMinInvoice(e.target.value)}
                data-testid="auto-fund-edit-min"
                className="mt-1.5 h-11"/>
            </div>
            <div>
              <Label>Daily cap (USD)</Label>
              <Input type="number" min={1} step={1000} value={dailyCap}
                onChange={e => setDailyCap(e.target.value)}
                data-testid="auto-fund-edit-daily"
                className="mt-1.5 h-11"/>
            </div>
            <div>
              <Label>Monthly cap (USD)</Label>
              <Input type="number" min={1} step={5000} value={monthlyCap}
                onChange={e => setMonthlyCap(e.target.value)}
                data-testid="auto-fund-edit-monthly"
                className="mt-1.5 h-11"/>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={!valid || submitting}
            data-testid="auto-fund-edit-submit"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {submitting ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
